import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonSelect,
  IonSelectOption,
  IonList,
  IonItem,
  IonLabel
} from '@ionic/react';
import { Aspect } from './AstroChart';
import './AspectList.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Aspects } from '../api';

interface AspectListProps {
  aspects: Aspect[];
}

const AspectList: React.FC<AspectListProps> = ({ aspects }) => {
  const { t } = useTranslation();
  const [typeFilter, setTypeFilter] = useState("all");
  const [planetFilter, setPlanetFilter] = useState<string>('all');

  const types = useMemo(() => Array.from(new Set(aspects.map((a) => a.type))), [aspects]);
  const planets = useMemo(
    () => Array.from(new Set(aspects.flatMap((a) => [a.from, a.to]))),
    [aspects]
  );

  const filtered = useMemo(
    () =>
      aspects
        .filter((a) => typeFilter === 'all' || a.type === typeFilter)
        .filter(
          (a) => planetFilter === 'all' || a.from === planetFilter || a.to === planetFilter
        )
        .sort((a, b) => b.strength - a.strength),
    [aspects, typeFilter, planetFilter]
  );

  return (
    <Card className="bg-white border-stone-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base md:text-lg text-slate-800">
          {t('aspects')}
        </CardTitle>
        <CardDescription className="text-xs md:text-sm text-slate-600">
          {t('aspectsDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <Select
            value={typeFilter}
            onValueChange={setTypeFilter}
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder={t('allTypes')} />
            </SelectTrigger>
            <SelectContent className='bg-white'>
              <SelectItem value="all">{t('allTypes')}</SelectItem>
              {types.map((a) => (
                <SelectItem key={a} value={a}>{t('aspect_' + a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={planetFilter}
            onValueChange={setPlanetFilter}
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder={t('allPlanets')} />
            </SelectTrigger>
            <SelectContent className='bg-white'>
              <SelectItem value="all">{t('allPlanets')}</SelectItem>
              {planets.map((p) => (
                <SelectItem key={p} value={p}>{t('planet_' + p)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filtered.map((aspect, idx) => (
            <div
              key={idx}
              className={
                aspect.type === Aspects.Conjunction
                  ? "p-3 border-l-4 border-blue-100 text-blue-800 bg-stone-50 rounded-r-lg hover:bg-stone-100 transition-colors"
                  : aspect.type === Aspects.Trine
                    ? "p-3 border-l-4 border-green-100 text-green-800 bg-stone-50 rounded-r-lg hover:bg-stone-100 transition-colors"
                    : aspect.type === Aspects.Sextile
                      ? "p-3 border-l-4 border-emerald-100 text-emerald-800 bg-stone-50 rounded-r-lg hover:bg-stone-100 transition-colors"
                      : "p-3 border-l-4 border-slate-100 text-slate-800 bg-stone-50 rounded-r-lg hover:bg-stone-100 transition-colors"
              }
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h4 className="font-medium text-slate-800">
                    {t('planet_' + aspect.from)} - {t('planet_' + aspect.to)}
                  </h4>
                  <p className="text-sm text-slate-600">
                    {t('orb')} {aspect.orb}° • {t('strength')} {aspect.strength}
                  </p>
                </div>
                <Badge
                  variant="secondary"
                  className={
                    aspect.type === Aspects.Conjunction
                      ? "bg-blue-100 text-blue-800"
                      : aspect.type === Aspects.Trine
                        ? "bg-green-100 text-green-800"
                        : aspect.type === Aspects.Sextile
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-slate-100 text-slate-800"
                  }
                >
                  {t('aspect_' + aspect.type)}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default AspectList;

